/* ───── View shell (top-level) ─────────────────────────────────────── */

.view {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
}

/* ───── Vertical stack: filter card → optional form → grid → kind strip ── */

.layout {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Filter card — same chrome/stripe as the port-calendar .filters card. */
.filterCard {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 1rem 1.1rem;
  background: var(--surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  position: relative;
  /* Clip the ::before stripe to the rounded card outline (matches the
     port-calendar .filters card). The multi-select popups are
     Portal-rendered into document.body so they escape this clip. */
  overflow: hidden;
}
/* Compact mode: edge-to-edge filter card. Matches the Calendar
   .filters compact override so the two views feel consistent. */
:global(html[data-compact="true"]) .filterCard {
  border-left: none;
  border-right: none;
  border-radius: 0;
  box-shadow: none;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}
:global(html[data-compact="true"]) .filterCard::before {
  border-radius: 0;
}
.filterCard::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: var(--gradient-primary);
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
  opacity: 0.85;
}

/* A label/control pair in the filter card. Mirrors Calendar.module.css .group. */
.group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.group > label:first-child {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: var(--text-secondary);
}
.groupRight { margin-left: auto; }

.searchInput {
  height: 38px;
  min-width: 220px;
  padding: 0 0.7rem;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: 0.85rem;
  color: var(--text-primary);
}
.searchInput:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
}

.checkRow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 0.6rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  color: var(--text-primary);
  background: var(--surface);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.checkRow input[type="checkbox"] { accent-color: var(--primary); }

.secondaryBtn {
  height: 38px;
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0 0.85rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: color var(--t-fast), background var(--t-fast), border-color var(--t-fast);
}
.secondaryBtn:hover {
  color: var(--text-primary);
  background: var(--surface-tinted);
  border-color: var(--border-strong);
}

/* ───── Slim toolbar above the events table ────────────────────────── */

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 0.85rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}
:global(html[data-compact="true"]) .toolbar {
  border-left: none;
  border-right: none;
  border-radius: 0;
  box-shadow: none;
  padding-left: 0.55rem;
  padding-right: 0.55rem;
}
.toolbarStats {
  font-size: 0.85rem;
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.toolbarStats strong { color: var(--text-primary); font-weight: 700; }

/* Rightmost header cell hosts + Add (always) and Clear (when filters
   active). The cell itself is non-clickable; the inner buttons are.
   Selectors include `.table th` / `.table td` to outweigh the generic
   th/td padding+text-align rules above (which are 1,1 specificity). */
.table th.thActionsCell,
.table td.actionCell {
  text-align: right;
  padding: 0.25rem 0.5rem;
  vertical-align: middle;
}
.thActionsCell { cursor: default !important; }
.thActionsCell:hover { color: var(--text-secondary) !important; }
.thActions {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.thAddBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--primary);
  background: var(--primary);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  line-height: 1;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.thAddBtn:hover { background: var(--primary-strong); border-color: var(--primary-strong); }

/* Header-cell variant in destructive red. Takes the place of .thAddBtn when
   one or more rows are selected; wider to accommodate the count badge. */
.thDeleteBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.6rem;
  border: 1px solid var(--error);
  background: var(--error);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  line-height: 1;
  font-size: 0.78rem;
  font-weight: 600;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.thDeleteBtn:hover {
  background: color-mix(in srgb, var(--error) 85%, black);
  border-color: color-mix(in srgb, var(--error) 85%, black);
}
.thDeleteCount {
  font-variant-numeric: tabular-nums;
}

/* Narrow leftmost selection column. Matches the table's standard
   horizontal cell padding so the checkbox column visually aligns
   with the rest of the grid instead of hugging the left edge. */
.table th.selectCell,
.table td.selectCell {
  width: 1%;
  padding: 0.5rem 0.85rem;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}
.table th.selectCell { padding: 0.6rem 0.85rem; cursor: default; }
.table th.selectCell:hover { color: var(--text-secondary) !important; }
.selectBox {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--primary);
  vertical-align: middle;
}

/* Highlight the row band when its checkbox is on, so a multi-row selection
   reads as a coherent group at a glance. */
.rowSelected td {
  background: color-mix(in srgb, var(--primary) 10%, transparent);
}

.thClearBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  height: 24px;
  padding: 0 0.55rem;
  border: 1px solid var(--error);
  background: transparent;
  color: var(--error);
  border-radius: var(--radius-sm);
  cursor: pointer;
  line-height: 1;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  transition: color var(--t-fast), background var(--t-fast);
}
.thClearBtn:hover {
  color: white;
  background: var(--error);
}

/* Per-row action buttons (edit + delete) in the rightmost body cell. */
.rowActions {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}
/* Square icon-only button matching .thAddBtn's chrome. */
.editBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--primary);
  background: var(--primary);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.editBtn:hover { background: var(--primary-strong); border-color: var(--primary-strong); }
.editBtn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Destructive variant of .editBtn. Used for per-row delete buttons in
   grids where the header bulk-delete already uses .thDeleteBtn so both
   surfaces share the red palette. */
.deleteRowBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--error);
  background: var(--error);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.deleteRowBtn:hover {
  background: color-mix(in srgb, var(--error) 85%, black);
  border-color: color-mix(in srgb, var(--error) 85%, black);
}
.deleteRowBtn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Add/edit modal dialog (Portal-rendered). Backdrop dim + blur shared
   with the cargo popup and the voyage-details modal — see global.css
   --popup-backdrop-* vars. */
.dialogBackdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: var(--popup-backdrop-bg);
  backdrop-filter: var(--popup-backdrop-blur);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: dlg-fade-in 0.18s ease-out;
}
@keyframes dlg-fade-in { from { opacity: 0; } to { opacity: 1; } }

.dialogCard {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 4rem);
  max-height: calc(100dvh - 4rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}
:global(html[data-compact="true"]) .dialogCard {
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}
.dialogCard::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: var(--gradient-primary);
}

.dialogHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.dialogTitle {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.005em;
}
.dialogClose {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  font-size: 1.2rem;
  line-height: 1;
  color: var(--text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color var(--t-fast), background var(--t-fast);
}
.dialogClose:hover { color: var(--text-primary); background: var(--surface-tinted); }

.dialogForm {
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  overflow-y: auto;
}
.dialogActions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.4rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--border);
}

.cancelBtn {
  height: 38px;
  padding: 0 0.95rem;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color var(--t-fast), background var(--t-fast), border-color var(--t-fast);
}
.cancelBtn:hover {
  color: var(--text-primary);
  background: var(--surface-tinted);
  border-color: var(--border-strong);
}

/* Destructive action in the dialog footer. Sits on the LEFT, separated
   from Cancel/Save by an auto-margin spacer — standard "destructive
   left, primary right" pattern so users can't reach it on autopilot. */
.dangerBtn {
  height: 38px;
  padding: 0 0.95rem;
  border: 1px solid var(--error);
  background: transparent;
  color: var(--error);
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color var(--t-fast), background var(--t-fast);
}
.dangerBtn:hover { color: white; background: var(--error); }
.dangerBtn:disabled { opacity: 0.5; cursor: not-allowed; }

.dialogActionsSpacer { flex: 1 1 auto; }

/* Two-step delete confirmation row replaces the regular dialog actions
   while the user is confirming. Tinted error background to signal the
   destructive scope of what's about to happen. */
.confirmRow {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.7rem;
  margin-top: 0.4rem;
  background: var(--error-bg);
  border: 1px solid color-mix(in srgb, var(--error) 35%, transparent);
  border-radius: var(--radius-md);
}
.confirmText {
  flex: 0 1 auto;
  font-size: 0.83rem;
  color: var(--error);
  font-weight: 500;
}

.dangerBtnSolid {
  height: 38px;
  padding: 0 0.95rem;
  border: 1px solid var(--error);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--t-fast), border-color var(--t-fast);
}
.dangerBtnSolid:hover { background: color-mix(in srgb, var(--error) 85%, black); border-color: color-mix(in srgb, var(--error) 85%, black); }
.dangerBtnSolid:disabled { opacity: 0.6; cursor: not-allowed; }

/* Header cells render a ColumnHeader button that fills the cell and
   handles its own padding + hover background, so the th itself contributes
   no padding/cursor of its own. */
.headerCell {
  padding: 0 !important;
  cursor: default !important;
}
.headerCell:hover { color: var(--text-secondary) !important; }

/* ───── Table (matches VesselsTable.module.css visuals) ────────────── */

.tableWrap {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  min-height: 0;
  position: relative;
}
:global(html[data-compact="true"]) .tableWrap {
  border-left: none;
  border-right: none;
  border-radius: 0;
  box-shadow: none;
}

.tableScroll {
  overflow: auto;
  flex-grow: 1;
  min-height: 0;
  contain: layout style paint;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  /* Auto layout: columns size to their content. We mark the "long-tail"
     column in each grid (Description / Terminal) with width:100% to
     absorb extra space so the table still fills its container without
     squashing the fixed columns. Trade-off: column widths can shift
     when filter state changes a row's content shape — acceptable for
     our row counts. */
  table-layout: auto;
}

.table thead {
  position: sticky;
  top: 0;
  background: var(--surface-tinted);
  z-index: 2;
}
.table thead tr { border-bottom: 1px solid var(--border); }
.table th {
  text-align: left;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  padding: 0.6rem 0.85rem;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.table th:hover { color: var(--primary); }
.table th.sortAsc::after  { content: " ▲"; opacity: 0.7; font-size: 0.7em; }
.table th.sortDesc::after { content: " ▼"; opacity: 0.7; font-size: 0.7em; }

.table tbody tr {
  border-top: 1px solid var(--border);
  transition: background var(--t-fast);
}
.table tbody tr:nth-child(odd) { background: rgba(99, 102, 241, 0.02); }
.table tbody tr:hover { background: rgba(99, 102, 241, 0.08); }
.table td {
  padding: 0.5rem 0.85rem;
  vertical-align: middle;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

/* Cell modifiers (apply to <th> and <td> alike). */
.center     { text-align: center; }
.code       { font-family: var(--font-mono); font-size: 0.82rem; }
.subtle     { color: var(--text-secondary); }
/* Padding/text-align live in the combined .table th.thActionsCell rule
   above so the higher specificity wins over .table th/td. */

.empty {
  padding: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: var(--text-muted);
  font-style: italic;
}

/* ───── Swatches ───────────────────────────────────────────────────── */

.swatch {
  position: relative;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid color-mix(in srgb, var(--text-muted) 30%, transparent);
  cursor: pointer;
  flex-shrink: 0;
  transition: transform var(--t-fast), border-color var(--t-fast);
  display: inline-block;
  padding: 0;
  background-color: transparent;
}
.swatch:hover { transform: scale(1.08); border-color: var(--primary); }
.swatchUnset {
  background-image: linear-gradient(45deg, var(--surface) 25%, transparent 25%),
                    linear-gradient(-45deg, var(--surface) 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, var(--surface) 75%),
                    linear-gradient(-45deg, transparent 75%, var(--surface) 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0;
  background-color: var(--surface-tinted);
}
.clearOne {
  width: 30px;
  height: 30px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-muted);
  font-size: 1.2rem;
  line-height: 1;
  border-radius: var(--radius-sm);
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color var(--t-fast), background var(--t-fast), border-color var(--t-fast);
}
.clearOne:hover {
  color: white;
  background: var(--error);
  border-color: var(--error);
}

/* Tri-state radio body for the Colour column's filter popup —
   "Any / With colour / Without colour". Mirrors the look of the
   .row entries in ColumnFilters' MultiCheckBody so it visually
   belongs in the popup. */
.radioGroup {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}
.radioRow {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.65rem;
  cursor: pointer;
  font-size: 0.83rem;
  color: var(--text-primary);
  border-radius: var(--radius-sm);
  transition: background var(--t-fast);
}
.radioRow:hover { background: var(--surface-hover); }
.radioRow input[type="radio"] { accent-color: var(--primary); }

.rowReadonly {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* Reserves the slot the delete × would occupy on custom rows so the edit
   button stays in the same column for both row sources. */
.rowReadonlySpacer {
  display: inline-block;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}

/* ───── Add-event form (collapsible row beneath the filter card) ──── */

.form {
  padding: 0.7rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface-tinted);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
}
:global(html[data-compact="true"]) .form {
  border-left: none;
  border-right: none;
  border-radius: 0;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
}
.formRow {
  display: flex;
  gap: 0.7rem;
  align-items: flex-end;
  flex-wrap: wrap;
}
.dateTimeStack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.formLabel {
  flex: 1 1 0;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.formLabel input,
.formLabel select {
  height: 34px;
  padding: 0 0.55rem;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: 0.85rem;
  color: var(--text-primary);
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
}
.formLabel input:focus,
.formLabel select:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
}
.formError {
  color: var(--error);
  background: var(--error-bg);
  border-radius: var(--radius-sm);
  padding: 0.4rem 0.55rem;
  font-size: 0.78rem;
}
.formActions {
  display: flex;
  align-items: flex-end;
}
.primary {
  background: var(--primary);
  color: white;
  border: none;
  height: 38px;
  padding: 0 0.95rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background var(--t-fast);
}
.primary:hover { background: var(--primary-strong); }
.primary:disabled { opacity: 0.55; cursor: not-allowed; }

/* ───── Date-event row chrome ──────────────────────────────────────── */

.kindChip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 0.55rem;
  border-radius: var(--radius-pill);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-primary);
  border: 1px solid color-mix(in srgb, var(--text-muted) 20%, transparent);
  white-space: nowrap;
}

.descMuted {
  color: var(--text-muted);
  font-style: italic;
}

.countryTag {
  font-family: var(--font-mono);
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--text-secondary);
  background: var(--surface-tinted);
  border: 1px solid var(--border);
  padding: 1px 0.35rem;
  border-radius: var(--radius-sm);
}

/* Add/edit dialog: Kind combobox + per-kind colour swatch on one row. */
.formKindRow {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}
.formKindRow > :first-child {
  flex: 1 1 0;
  min-width: 0;
}
.formKindSwatch {
  position: relative;
  width: 32px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  cursor: pointer;
}
.formKindSwatch[aria-disabled="true"] {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--surface-tinted) !important;
}
.formKindSwatch input[type="color"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: inherit;
  border: none;
  background: transparent;
  padding: 0;
}
